import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Brand, ExpectedEpcsInput, PrintedTag, SkuTagGeneration, UnexpectedEpcsInput } from '@common/services/commondata';
import { oDataService } from '@common/services/odata';
import { SignalRService } from '@common/services/signalR';
import { Pagination } from '@common/types/pagination.types';
import { environment } from 'environments/environment';
import { BehaviorSubject, forkJoin, Observable, tap, map, timer, switchMap, takeUntil, Subject, catchError, of } from 'rxjs';
import { GetUnscannedTunnelDataRequest } from './details/detail.model';

@Injectable({ providedIn: 'root' })
export class GoodsInboundService {
    // Private
    private _filters: BehaviorSubject<any> = new BehaviorSubject({});
    private _pagination: BehaviorSubject<Pagination | null> = new BehaviorSubject(null);
    private _product: BehaviorSubject<any | null> = new BehaviorSubject(null);
    private _products: BehaviorSubject<any[] | null> = new BehaviorSubject(null);
    private _tunnels: BehaviorSubject<any[] | null> = new BehaviorSubject(null);
    private _paginationScannedTags: BehaviorSubject<Pagination | null> = new BehaviorSubject(null);
    private _productScannedTags: BehaviorSubject<any | null> = new BehaviorSubject(null);
    private _productsScannedTags: BehaviorSubject<any[] | null> = new BehaviorSubject(null);
    private _productsScannedTagsNumber: BehaviorSubject<string | null> = new BehaviorSubject(null);
    private _productsCheckedEpcsNumber: BehaviorSubject<any[] | null> = new BehaviorSubject(null);

    private _productUnexpectedTags: BehaviorSubject<any | null> = new BehaviorSubject(null);
    private _productsUnexpectedTags: BehaviorSubject<any[] | null> = new BehaviorSubject(null);

    private controller: string = 'GoodsInbound';
    private tunnelScanningController: string = 'TunnelScanning';
    private tunnelController: string = 'Tunnel';
    private warehouseLocationController: string = "WarehouseLocation";
    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _oDataService: oDataService,
        private _signalRService: SignalRService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for filters
     */
    get filters() {
        return this._filters.value;
    }

    /**
     * Setter for filters
     */
    set filters(values: any) {
        this._filters.next(values);
    }

    /**
    * Getter for Pagination
    */
    get paginationScannedTags() {
        return this._paginationScannedTags.value;
    }

    /**
     * Setter for Pagination
     */
    set paginationScannedTags(values) {
        this._paginationScannedTags.next(values);
    }

    /**
     * Getter for pagination
     */
    get paginationScannedTags$(): Observable<Pagination> {
        return this._paginationScannedTags.asObservable();
    }

    public getCurrentScannedProducts(): any[] {
        // Safely return the current value, defaulting to an empty array
        return this._productsScannedTags.value ?? [];
    }
    /**
     * Getter for products
     */
    get productsScannedTags$(): Observable<any[]> {
        return this._productsScannedTags.asObservable();
    }
    public updateScannedProducts(products): void {

        const updatedProductsWithColor = products.map(p => ({
            ...p,
            // Calculate the color class based on the product's checked state
            colorClass: this.getColorClassForProduct(p)
        }));
        this._productsScannedTags.next(updatedProductsWithColor);
        // You may also need to re-trigger the count update here if it's not done in the detail component:
        // const newCount = products.flatMap(p => p.epcsWeb.filter(e => e.isChecked)).length;
        // this._productsScannedTagsNumber.next(String(newCount));
        // this._productsCheckedEpcsNumber.next(products.flatMap(p => p.epcsWeb.filter(e => e.isChecked).map(e => e.epcsName)));
    }
    getColorClassForProduct(product): string {
        const epcsWeb = product.epcsWeb || [];
        const total = epcsWeb.length;
        const selected = epcsWeb.filter(e => e.isChecked).length;

        if (total === 0) {
            return '';
        }
        if (selected === total) {
            return 'color-full-selection'; // Green
        }
        if (selected > 0) {
            return 'color-partial-selection'; // Pale Blue
        }
        return ''; // Normal (Not selected)
    }
    /**
     * Getter for product
     */
    get productUnexpectedTags$(): Observable<any> {
        return this._productUnexpectedTags.asObservable();
    }

    /**
     * Getter for products
     */
    get productsUnexpectedTags$(): Observable<any[]> {
        return this._productsUnexpectedTags.asObservable();
    }
    public set SetUnexpectedTags(values) {
        this._productsUnexpectedTags.next(values);
    }

    /**
   * Getter for Pagination
   */
    get pagination() {
        return this._pagination.value;
    }

    /**
     * Setter for Pagination
     */
    set pagination(values) {
        this._pagination.next(values);
    }

    /**
     * Getter for pagination
     */
    get pagination$(): Observable<Pagination> {
        return this._pagination.asObservable();
    }

    /**
     * Getter for product
     */
    get product$(): Observable<any> {
        return this._product.asObservable();
    }

    /**
     * Getter for products
     */
    get products$(): Observable<any[]> {
        return this._products.asObservable();
    }
    /**
       * Getter for tunnels
       */
    get tunnels$(): Observable<any[]> {
        return this._tunnels.asObservable();
    }

    /**
     * Getter for products
     */
    get productsScannedTagsNumber$(): Observable<string> {
        return this._productsScannedTagsNumber.asObservable();
    }

    set _productsScannedTagsNumber$(values) {
        this._productsScannedTagsNumber.next(values);
    }


    /**
     * Getter for products
     */
    get productsCheckedEpcsNumber$(): Observable<any[]> {
        return this._productsCheckedEpcsNumber.asObservable();
    }

    set _productsCheckedEpcsNumber$(values) {
        this._productsCheckedEpcsNumber.next(values);
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get products
     * @param reload
     */
    getProductsWithPagination(reload?: boolean): Observable<any> {
        if (reload) {
            this.pagination = {
                length: 0,
                page: 0,
                size: 100,
                sort: 'firstSubmittedOn',
                order: 'desc'
            }
            this.filters = {};
        }
        return forkJoin({
            products: this.getProducts(this.filters, this.pagination),
        }).pipe(
            tap((response) => {
                this._pagination.next({ ...this.pagination, length: response.products["@odata.count"] });
                this._products.next(response.products["value"]);
            })
        );
    }

    getProductDetailWithScannedTags(id?: string, tunnelCode = "") {

        let epc = ''
        if (this._productsCheckedEpcsNumber.value) {
            epc = this._productsCheckedEpcsNumber.value.map(x => x).join(',')
        }

        return forkJoin({
            products: this.getPurchaseOrderWeb(id, epc, tunnelCode),
        }).pipe(
            tap((response) => {
                response.products.map(x => {
                    x.SelectedQty = x.epcsWeb.filter(x => x.isChecked).length
                    x.TotalQty = (x.epcs).length
                })

                let len = 0
                response.products.forEach(x => {
                    len += x.SelectedQty
                })
                this._productsScannedTags.next(response.products);

                this._productsScannedTagsNumber.next(len.toString());
            })
        );
    }
    updatedTunnelScanData(tunnelCode: string = ""): Observable<TunnelScanResult> {
        const norm = (s: string) => (s ?? "").trim().toUpperCase();

        // 1) Build exclusion list for the backend (user checked + already checked in UI)
        const userChecked = (this._productsCheckedEpcsNumber?.value ?? [])
            .map(norm).filter(Boolean);

        const currentGroups = this._productsScannedTags?.value ?? [];
        const previouslyChecked = currentGroups
            .flatMap((g: any) => g?.epcsWeb ?? [])
            .filter((t: any) => !!t?.isChecked)
            .map((t: any) => norm(t?.epcsName ?? t?.epc ?? t?.name ?? ""))
            .filter(Boolean);

        const alreadyScannedTags = Array.from(new Set<string>([...userChecked, ...previouslyChecked]));

        const request: GetUnscannedTunnelDataRequest = { tunnelCode, alreadyScannedTags };

        return this.getTunnelScannedData(request).pipe(
            map((response: string[] = []): TunnelScanResult => {
                // 2) NEW items from server for this tick (server already excluded "alreadyScannedTags")
                const newScanned = Array.from(new Set(response.map(norm).filter(Boolean)));

                // 3) Build the cumulative "checked" set (persist checks across ticks)
                const cumulativeChecked = new Set<string>([...userChecked, ...previouslyChecked, ...newScanned]);

                // 4) Ensure each group's epcsWeb contains all inbound EPCs (append missing),
                //    then mark isChecked based on the cumulative set
                let totalSelected = 0;
                const updatedGroups = currentGroups.map((group: any) => {
                    const epcs: string[] = (group?.epcs ?? []).map(norm).filter(Boolean);

                    // Build a Map for quick lookup of existing epcsWeb by key
                    const epcsWebArr: any[] = Array.isArray(group?.epcsWeb) ? group.epcsWeb : [];
                    const byKey = new Map<string, any>();
                    for (const t of epcsWebArr) {
                        const key = norm(t?.epcsName ?? t?.epc ?? t?.name ?? "");
                        if (key) byKey.set(key, t);
                    }

                    // Append any missing EPCs from inbound list into epcsWeb
                    for (const epc of epcs) {
                        if (!byKey.has(epc)) {
                            const added = { epcsName: epc, isChecked: false };
                            byKey.set(epc, added);
                        }
                    }

                    // Produce the updated epcsWeb array with correct isChecked flags
                    const updatedEpcsWeb = Array.from(byKey.entries())
                        .sort((a, b) => a[0].localeCompare(b[0])) // stable ordering by EPC (optional)
                        .map(([key, tag]) => {
                            const nextChecked = cumulativeChecked.has(key);
                            return nextChecked === !!tag.isChecked ? tag : { ...tag, isChecked: nextChecked };
                        });

                    const selectedCount = updatedEpcsWeb.reduce((acc, t) => acc + (t.isChecked ? 1 : 0), 0);
                    totalSelected += selectedCount;
                    const tempProduct = {
                        ...group,
                        epcsWeb: updatedEpcsWeb,
                        SelectedQty: selectedCount
                    };
                    return {
                        ...tempProduct,
                        colorClass: this.getColorClassForProduct(tempProduct),
                        TotalQty: Array.isArray(group?.epcs) ? group.epcs.length : group?.TotalQty,
                    };
                });

                // 5) Emit combined groups + global count
                this._productsScannedTags.next(updatedGroups);
                this._productsScannedTagsNumber.next(String(totalSelected));

                // Return a stable shape; StartScan() can use newScanned for unexpected
                return { updatedGroups, newScanned };
            }),
            catchError(err => {
                console.error('updatedTunnelScanData error', err);
                // keep stream alive with a consistent shape
                return of<TunnelScanResult>({
                    updatedGroups: this._productsScannedTags?.value ?? [],
                    newScanned: [],
                });
            })
        );
    }
    public resetScanState(): void {
        // 1. Reset the main product list to an empty array
        this._productsScannedTags.next([]);

        // 2. Reset the total count
        this._productsScannedTagsNumber.next('0');

        // 3. Reset the checked EPCs list
        this._productsCheckedEpcsNumber.next([]);

        // 4. (Optional but recommended) Clear localStorage if you're using it for persistence
        localStorage.removeItem('purchaseOrder');
    }
    getInboundItemDetailByPurchaseOrders(id?: string, tunnelCode = "") {

        // let epc = ''
        // if (this._productsCheckedEpcsNumber.value)
        // {
        //     epc = this._productsCheckedEpcsNumber.value.map(x => x).join(',')
        // }

        return forkJoin({
            products: this.getGoodInboundItemDetail(id, tunnelCode),
        }).pipe(
            tap((response) => {
                response.products.map(x => {
                    x.SelectedQty = x.epcsWeb.filter(x => x.isChecked).length
                    x.TotalQty = x.epcsWeb.length
                })

                let len = 0
                response.products.forEach(x => {
                    len += x.SelectedQty
                })
                this._productsScannedTags.next(response.products);

                this._productsScannedTagsNumber.next(len.toString());
                this._productsUnexpectedTags.next([]);
            })
        );
    }
    getProductDetailWithUnexpectedTagsReason(
        id?: string,
        newScanned: string[] = [],          // <— pass the “new-only” EPCs from your scan call
        tunnelCode: string = ""
    ) {
        const norm = (s: string) => (s ?? "").trim().toUpperCase();

        // 1) Build a set of *all inbound EPCs* (from page-load data)
        const groups = this._productsScannedTags?.value ?? [];
        const inboundSet = new Set<string>([
            // raw epcs arrays on each group
            ...groups
                .flatMap((g: any) => (Array.isArray(g?.epcs) ? g.epcs : []))
                .map(norm),
            // epcsWeb names as fallback
            ...groups
                .flatMap((g: any) => g?.epcsWeb ?? [])
                .map((t: any) => norm(t?.epcsName ?? t?.epc ?? "")),
        ].filter(Boolean));

        // 2) Already in unexpected → skip to avoid duplicates when we append
        const existingUnexpected: UnexpectedTag[] = this._productsUnexpectedTags?.value ?? [];
        const existingUnexpectedSet = new Set<string>(
            existingUnexpected.map(u => norm(u?.epc)).filter(Boolean)
        );

        // 3) Keep only fresh unexpected EPCs:
        //    from newScanned, NOT in inbound, NOT already in unexpected
        const candidates = Array.from(new Set(newScanned.map(norm).filter(Boolean)))
            .filter(e => !inboundSet.has(e))
            .filter(e => !existingUnexpectedSet.has(e));

        // if (candidates.length === 0) {
        //     // nothing to fetch → just return current state
        //     return of(existingUnexpected);
        // }

        // 4) Fetch details for the fresh unexpected EPCs
        return forkJoin({
            productsUnexpectedTags: this.getProductsunexpectedEpcs(id, candidates, tunnelCode),
        }).pipe(
            map(({ productsUnexpectedTags }) => {
                const incoming: UnexpectedTag[] = (productsUnexpectedTags ?? []).map((x: any) => ({
                    ...x,
                    expandedDetails: false, // UI helper
                }));

                // 5) Append immutably and dedupe by EPC (preserve existing entries’ state)
                const byEpc = new Map<string, UnexpectedTag>();
                for (const u of existingUnexpected) byEpc.set(norm(u.epc), u);
                for (const u of incoming) {
                    const key = norm(u.epc);
                    if (!byEpc.has(key)) byEpc.set(key, u);
                }
                const nextUnexpected = Array.from(byEpc.values());

                // IMPORTANT: do NOT mutate with push; emit a *new array*
                this._productsUnexpectedTags.next(nextUnexpected);
                // Optional count:
                // this._productsUnexpectedTagsNumber?.next(String(nextUnexpected.length));

                return nextUnexpected;
            }),
            catchError(err => {
                console.error('getProductDetailWithUnexpectedTags error', err);
                return of(this._productsUnexpectedTags?.value ?? []);
            })
        );
    }

    getProductDetailWithUnexpectedTags(id?: string, tunnelCode = "") {

        let epcs: any[] = [];
        let alreadySelectedEpcs: any[] = [];
        if (this._productsUnexpectedTags.value != null) {
            this._productsScannedTags.value.map(x => x.epcsWeb.filter(y => y.isChecked).map(y => alreadySelectedEpcs.push(y.epcsName)))
            epcs = this._productsUnexpectedTags.value.map(x => x.epc).filter(y => !alreadySelectedEpcs.includes(y));


        }

        return forkJoin({
            productsUnexpectedTags: this.getProductsunexpectedEpcs(id, epcs, tunnelCode),
        }).pipe(
            tap((response) => {
                response.productsUnexpectedTags.map(x => {
                    x.expandedDetails = false
                })
                this._productsUnexpectedTags.next(response.productsUnexpectedTags);
            })
        );
    }

    /**
     * Get products
     *
     * @param filters
     * @param pagination
     */
    getProducts(filters, pagination) {
        return this._httpClient.get<any[]>(`${environment.oDataUrl}/odata/` + this.controller + this._oDataService.setMultipleValueFilters(filters, pagination));
    }

    /**
     * Get Purchase Order
     *
     */
    // getPurchaseOrder(id) {
    //     return this._httpClient.get<any[]>(`${environment.oDataUrl}/api/` + this.controller + `/purchaseOrderId?purchaseOrderIds=` + id);
    // }

    getPurchaseOrderWeb(id, epcs, tunnelCode = "") {
        let orderRequest: ExpectedEpcsInput = {
            checkedEPCs: epcs,
            purchaseOrderIds: id
        }
        return this._httpClient.post<any[]>(`${environment.oDataUrl}/api/` + this.controller + `/purchaseOrderIdWebPost?tunnelCode=${tunnelCode}`, orderRequest);
        //return this._httpClient.get<any[]>(`${environment.oDataUrl}/api/` + this.controller + `/purchaseOrderIdWeb?purchaseOrderIds=` + id + `&checkedEPCs=` + epcs);
    }

    getGoodInboundItemDetail(id, tunnelCode = "") {
        let orderRequest: ExpectedEpcsInput = {
            checkedEPCs: '',
            purchaseOrderIds: id
        }
        return this._httpClient.post<any[]>(`${environment.oDataUrl}/api/` + this.controller + `/GetInboundItemDetails?tunnelCode=${tunnelCode}`, orderRequest);
    }

    getProductsunexpectedEpcs(id, epcs, tunnelCode = "") {
        let orderRequest: UnexpectedEpcsInput = {
            Epcs: epcs,
            PurchaseOrderIds: id ? id.split(',').map(Number) : [],
        }
        return this._httpClient.post<any[]>(`${environment.oDataUrl}/api/` + this.controller + `/unexpectedEpcsWeb?tunnelCode=${tunnelCode}`, orderRequest);
    }

    newPallet(): Observable<any> {
        var countryId = localStorage.getItem("inboundSelectedCountryId");
        return this._httpClient.post(`${environment.oDataUrl}/api/Pallet/CreateNew?countryId=` + countryId, {});
    }
    saveOrder(order) {
        return this._httpClient
            .post<any>(
                `${environment.oDataUrl}/api/` + this.controller,
                order
            );
    }
    getTunnelsWithPagination(reload?: boolean): Observable<any> {
        if (reload) {
            this.pagination = {
                length: 0,
                page: 0,
                size: 100,
                sort: '',
                order: ''
            }
            this.filters = {};
        }
        return forkJoin({
            products: this.getTunnelsInfo(),
        }).pipe(
            tap((response) => {
                //this._pagination.next({ ...this.pagination, length: response.products["@odata.count"] });
                this._tunnels.next(response.products["value"]);
            })
        );
    }

    getTunnelsInfo() {
        return this._httpClient.get<any[]>(`${environment.oDataUrl}/api/` + this.tunnelController);
    }
    getTunnelCommon() {
        return this.getTunnelsInfo().pipe(
            map((response) => {
                this._tunnels.next(response);
            })
        );
    }
    getTunnelScannedData(request: GetUnscannedTunnelDataRequest) {
        return this._httpClient.post<any[]>(`${environment.oDataUrl}/api/` + this.tunnelScanningController + `/GetUnscannedTunnelData`, request);
    }

    removeScannedData(tunnelCode = "") {
        return this._httpClient.post<any>(`${environment.oDataUrl}/api/` + this.tunnelScanningController + `/delete?tunnelCode=${tunnelCode}`, {});
    }

    startScanning(tunnelCode = "") {
        return this._httpClient.post<any>(`${environment.oDataUrl}/api/` + this.tunnelScanningController + `/start?tunnelCode=${tunnelCode}`, {});
    }

    stopScanning(tunnelCode = "") {
        return this._httpClient.post<any>(`${environment.oDataUrl}/api/` + this.tunnelScanningController + `/stop?tunnelCode=${tunnelCode}`, {});
    }

    getScanningStatus(tunnelCode = "") {
        return timer(1, 5000).pipe(
            switchMap((seconds) => {
                return this._httpClient.get(`${environment.oDataUrl}/api/` + this.tunnelScanningController + `/NotifyStatusScanningGoodsInbound?tunnelCode=${tunnelCode}`,
                    {

                    })
            })
        );
    }
    getScanningStatusO() {
        return this._httpClient.get(`${environment.oDataUrl}/api/` + this.tunnelScanningController + `/NotifyStatusScanningGoodsInbound`,
            {

            })
    }
    getLocationByCountryId(id, locationType) {
        return this._httpClient.get<any[]>(`${environment.oDataUrl}/api/` + this.warehouseLocationController + `/GetWarehouseLocationByCountryIdAndType?countryId=` + id + '&locationType=' + locationType);
    }
}

type UnexpectedTag = {
    epc: string;
    skuCode: string | null;
    processId: number;
    processTypeCode: string | null;
    locationId: number | null;
    locationTypeCode: string | null;
    poNumber: string | null;
    status: string;
    expandedDetails?: boolean;
};

type TunnelScanResult = {
    updatedGroups: any[];
    newScanned: string[];
};